import type { ReactElement } from 'react'
import React from 'react'
import { NavLink } from 'react-router-dom'

import { NotificationIndicator } from '@components/common/NotificationIndicator'
import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { SECTION_NAMES } from '@constants/sectionNames'
import type { IconComponent } from '@fourthwall/components'
import {
  AnalyticsFilledIcon,
  AnalyticsIcon,
  AppFilledIcon,
  AppIcon,
  ApparelFilledIcon,
  ApparelIcon,
  CogFilledIcon,
  CogIcon,
  HomeFilledIcon,
  HomeIcon,
  MoneyFilledIcon,
  MoneyIcon,
  StarFilledIcon,
  StarIcon,
  TagFilledIcon,
  TagIcon,
  ThemeFilledIcon,
  ThemeIcon,
} from '@fourthwall/icons'
import { useUnfulfilledOrdersCountQuery } from '@modules/Dashboard/hooks/useUnfulfilledOrdersCountQuery'
import { routing } from '@utils/routing'

import { useCurrentShopQuery } from './useCurrentShopQuery'

export type NavigationItem = {
  label: string
  to: string
  icon?: IconComponent
  activeIcon?: IconComponent
  rightIcon?: ReactElement
  tag?: string
  as?: 'a' | 'button' | typeof NavLink
  exact?: boolean
  hidden?: boolean
  items?: NavigationItem[]
}

export const useNavigations = () => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { hasPermission } = useDashboardPermissions()
  const { currentShopQuery } = useCurrentShopQuery()

  const { unfulfilledOrderCountQuery } = useUnfulfilledOrdersCountQuery(undefined, {
    enabled: hasPermission('contributions.fulfillments.view'),
  })
  const ordersToManuallyFulfill = unfulfilledOrderCountQuery.data?.unfulfilledOrdersCount

  const shouldOnboarding =
    dashboardFeatureFlags.themeEditorOnboarding.enabled &&
    !currentShopQuery.data?.metafields['theme-editor-visited'] &&
    !currentShopQuery.data?.metafields['created-by-agency']

  const getNavigations = (): NavigationItem[] => [
    {
      label: SECTION_NAMES.home,
      to: routing.home,
      icon: HomeIcon,
      activeIcon: HomeFilledIcon,
      exact: true,
      hidden: !hasPermission('home'),
    },
    {
      label: 'Orders',
      to: routing.contributions.self,
      icon: MoneyIcon,
      activeIcon: MoneyFilledIcon,
      hidden: !hasPermission('contributions'),
      items: [
        {
          label: 'All orders',
          to: routing.contributions.orders.all,
          hidden: !hasPermission('contributions.orders.view'),
        },
        {
          label: 'Thank yous',
          to: routing.contributions.thankYous.self,
          hidden: !hasPermission('contributions.thankYous'),
        },
        {
          label: 'To be shipped',
          to: routing.contributions.fulfillment,
          rightIcon: ordersToManuallyFulfill ? (
            <NotificationIndicator>{ordersToManuallyFulfill}</NotificationIndicator>
          ) : undefined,
          hidden: !hasPermission('contributions.fulfillments.view'),
        },
      ],
    },
    {
      label: 'Products',
      to: routing.products.self,
      icon: ApparelIcon,
      activeIcon: ApparelFilledIcon,
      hidden: !hasPermission('products.view') && !hasPermission('collections.view'),
      items: [
        {
          label: 'All products',
          to: routing.products.all.self,
          hidden: !hasPermission('products.view'),
        },
        {
          label: 'Collections',
          to: routing.products.collections.self,
          hidden: !hasPermission('collections.view'),
        },
      ],
    },
    {
      label: 'Promotions',
      to: routing.promotions.self,
      icon: TagIcon,
      activeIcon: TagFilledIcon,
      hidden:
        !hasPermission('promotions.shopPromoCodes') && !hasPermission('promotions.giveawayLinks'),
    },
    {
      label: 'Memberships',
      to: routing.memberships.self,
      icon: StarIcon,
      activeIcon: StarFilledIcon,
      hidden: !dashboardFeatureFlags.memberships.enabled || !hasPermission('memberships'),
      items: [
        {
          label: 'Overview',
          to: routing.memberships.overview,
          hidden: !hasPermission('memberships.overview'),
        },
        {
          label: 'Posts',
          to: routing.memberships.posts.self,
        },
        {
          label: 'Messages',
          to: routing.memberships.messages.self,
          hidden:
            !dashboardFeatureFlags.membershipsMessages.enabled ||
            !hasPermission('memberships.messages'),
        },
        {
          label: 'Video series',
          to: routing.memberships.series.self,
        },
        {
          label: 'Members',
          to: routing.memberships.members.self,
          hidden: !hasPermission('memberships.members'),
        },
        {
          label: 'Perks & tiers',
          to: routing.memberships.perks.self,
          hidden: !hasPermission('memberships.perksAndTiers'),
        },
        {
          label: 'Settings',
          to: routing.memberships.settings.self,
          hidden: !hasPermission('memberships.settings'),
        },
      ],
    },
    {
      label: 'Memberships',
      to: routing.membershipsEnable.self,
      icon: StarIcon,
      activeIcon: StarFilledIcon,
      hidden: dashboardFeatureFlags.memberships.enabled || !hasPermission('memberships.setup'),
    },
    {
      label: SECTION_NAMES.themeEditor,
      to: shouldOnboarding ? routing.onboarding : routing.themeEditorV2.self,
      as: shouldOnboarding ? 'a' : NavLink,
      icon: ThemeIcon,
      activeIcon: ThemeFilledIcon,
      hidden: !hasPermission('themeEditor'),
    },
    {
      label: 'Analytics',
      to: routing.analytics.self,
      icon: AnalyticsIcon,
      activeIcon: AnalyticsFilledIcon,
      hidden: !hasPermission('analytics'),
      items: [
        {
          label: 'Overview',
          to: routing.analytics.overview.self,
          hidden: !hasPermission('analytics'),
        },
        {
          label: 'Reports',
          to: routing.analytics.reports.self,
          hidden: !hasPermission('analytics'),
        },
      ],
    },
    {
      label: 'Analytics v2',
      to: routing.analyticsV2.self,
      icon: AnalyticsIcon,
      activeIcon: AnalyticsFilledIcon,
      tag: 'Beta',
      hidden: !(hasPermission('analytics') && dashboardFeatureFlags.analyticsV2.enabled),
      items: [
        {
          label: 'Overview',
          to: routing.analyticsV2.overview.self,
          hidden: !(hasPermission('analytics') && dashboardFeatureFlags.analyticsV2.enabled),
        },
        {
          label: 'Reports',
          to: routing.analyticsV2.reports.self,
          hidden: !(hasPermission('analytics') && dashboardFeatureFlags.analyticsV2.enabled),
        },
      ],
    },
    {
      label: SECTION_NAMES.apps,
      to: routing.apps.self,
      icon: AppIcon,
      activeIcon: AppFilledIcon,
      hidden: !hasPermission('apps'),
    },
    {
      label: 'Settings',
      to: routing.settings.self,
      icon: CogIcon,
      activeIcon: CogFilledIcon,
      hidden: !hasPermission('settings'),
    },
  ]

  const getFirstVisibleNavigation = (scope?: string) => {
    const navigations = getNavigations()
    const targetNavigation = scope
      ? navigations.find((navigation) => navigation.to === scope)
      : navigations.find((navigation) => !navigation.hidden)

    if (!targetNavigation) {
      return routing.home
    }

    const targetItem = targetNavigation.items?.find((item) => !item.hidden)

    return targetItem?.to ?? targetNavigation.to
  }

  return {
    navigations: getNavigations(),
    getFirstVisibleNavigation,
  }
}
